<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAdmin } from '@/stores/admin'
import type { DataImportJobDetail } from '@/stores/imports'
import {
  formatPlatformName,
  getImportStatusColor,
  buildCloudRunUrl,
  buildCloudRunLogsUrl,
  buildApifyUrl,
  slugify,
} from '@/lib/utils'

const props = defineProps<{
  importJob: DataImportJobDetail
}>()

const emit = defineEmits<{
  close: []
  viewBrand: [slug: string]
}>()

const admin = useAdmin()

// Collapsible sections - only Inputs & Stats open by default
const showInputs = ref(true)
const showStats = ref(true)
const showLinks = ref(false)
const showDetails = ref(false)
const showError = ref(false)
const showRunHistory = ref(false)

// Track which runs are expanded
const expandedRuns = ref<Record<string, boolean>>({})

function toggleRunExpanded(runId: string) {
  expandedRuns.value[runId] = !expandedRuns.value[runId]
}

function isRunExpanded(runId: string) {
  return !!expandedRuns.value[runId]
}

// Track expanded sections per run: { runId: { stats: boolean, links: boolean, details: boolean, error: boolean } }
const runSections = ref<
  Record<string, { stats: boolean; links: boolean; details: boolean; error: boolean }>
>({})

// Initialize run sections when job_runs changes
watch(
  () => props.importJob.job_runs,
  (runs) => {
    for (const run of runs) {
      if (!runSections.value[run.id]) {
        runSections.value[run.id] = { stats: true, links: false, details: false, error: false }
      }
    }
  },
  { immediate: true },
)

function getRunSections(runId: string) {
  return runSections.value[runId] ?? { stats: true, links: false, details: false, error: false }
}

function toggleRunSection(runId: string, section: 'stats' | 'links' | 'details' | 'error') {
  if (!runSections.value[runId]) {
    runSections.value[runId] = { stats: true, links: false, details: false, error: false }
  }
  runSections.value[runId][section] = !runSections.value[runId][section]
}

// Format timestamps
function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

// Calculate duration between two timestamps
function formatDuration(startStr: string | null, endStr: string | null): string {
  if (!startStr || !endStr) return '-'
  const start = new Date(startStr)
  const end = new Date(endStr)
  const diffMs = end.getTime() - start.getTime()
  if (diffMs < 0) return '-'

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) return `${hours}h ${minutes % 60}m`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

// Platform display name
const platformName = computed(() => {
  const platform = admin.platforms.find((p) => p.code === props.importJob.platform)
  return platform ? formatPlatformName(platform.name) : props.importJob.platform
})

// External links
const cloudRunUrl = computed(() =>
  buildCloudRunUrl(props.importJob.cloud_run_job_id, props.importJob.cloud_run_execution_id),
)
const cloudRunLogsUrl = computed(() =>
  buildCloudRunLogsUrl(
    props.importJob.cloud_run_job_id,
    props.importJob.cloud_run_execution_id,
    props.importJob.task_ix,
    props.importJob.job_runs?.at(-1)?.started_ts ?? props.importJob.scheduled_ts,
  ),
)
const apifyUrl = computed(() => buildApifyUrl(props.importJob.apify_platform_run_id))

const hasLinks = computed(
  () =>
    cloudRunUrl.value || cloudRunLogsUrl.value || apifyUrl.value || props.importJob.scraping_url,
)

// Pre-compute URLs for all job runs
const jobRunsWithUrls = computed(() =>
  props.importJob.job_runs.map((run) => ({
    ...run,
    cloudRunUrl: buildCloudRunUrl(run.cloud_run_job_id, run.cloud_run_execution_id),
    cloudRunLogsUrl: buildCloudRunLogsUrl(
      run.cloud_run_job_id,
      run.cloud_run_execution_id,
      run.task_ix,
      run.started_ts,
    ),
    apifyUrl: buildApifyUrl(run.apify_platform_run_id),
    duration: formatDuration(run.started_ts, run.completed_ts),
  })),
)

// View brand in slideover
function viewBrand() {
  const slug =
    props.importJob.brand_slug || (props.importJob.brand ? slugify(props.importJob.brand) : null)
  if (slug) {
    emit('viewBrand', slug)
  }
}

// Copy text to clipboard
function copyToClipboard(text: string | null | undefined) {
  if (!text || !navigator.clipboard) return
  navigator.clipboard.writeText(text).catch(() => {})
}
</script>

<template>
  <div class="p-6 space-y-4 h-full overflow-y-auto">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <UBadge :color="getImportStatusColor(importJob.status)" size="lg">
          {{ importJob.status }}
        </UBadge>
        <UBadge
          v-if="importJob.failure_cnt && importJob.failure_cnt > 0"
          color="error"
          variant="subtle"
          size="sm"
        >
          {{ importJob.failure_cnt }} {{ importJob.failure_cnt === 1 ? 'retry' : 'retries' }}
        </UBadge>
      </div>
      <UButton icon="i-lucide-x" color="neutral" variant="ghost" @click="emit('close')" />
    </div>

    <!-- Inputs -->
    <div class="border border-muted/20 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/5 transition-colors"
        @click="showInputs = !showInputs"
      >
        <UIcon
          :name="showInputs ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-muted"
        />
        <span class="text-sm font-medium">Inputs</span>
      </button>
      <div v-if="showInputs" class="px-3 pb-3 space-y-2 text-sm">
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Platform</span>
          <UButton size="xs" color="neutral" variant="soft">{{ platformName }}</UButton>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Brand</span>
          <div class="flex items-center gap-1">
            <span>{{ importJob.brand || '-' }}</span>
            <UButton
              v-if="importJob.brand"
              icon="i-lucide-eye"
              size="xs"
              variant="ghost"
              color="neutral"
              @click="viewBrand"
            />
          </div>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Platform Advertiser ID</span>
          <span class="font-mono text-xs">{{
            importJob.platform_advertiser_identifier || '-'
          }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Method</span>
          <span>{{ importJob.method || '-' }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Country</span>
          <span>{{ importJob.country_code || 'ALL' }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Date Range</span>
          <span>{{ importJob.date_from || '-' }} → {{ importJob.date_to || '-' }}</span>
        </div>
        <template v-if="importJob.extra_kwargs">
          <div
            v-for="(value, key) in importJob.extra_kwargs"
            :key="key"
            class="flex items-start gap-2"
          >
            <span class="text-muted shrink-0">{{ key }}</span>
            <div class="flex-1 text-right">
              <template v-if="Array.isArray(value)">
                <template v-for="(item, idx) in value" :key="idx">
                  <div v-if="item !== null" class="font-mono text-xs">{{ item }}</div>
                </template>
                <div v-if="key === 'relevant_profile_names'" class="text-xs italic text-muted mt-1">
                  {{
                    value.includes(null)
                      ? '+ also matches ads without profile name'
                      : '(strict: ads without profile name are excluded)'
                  }}
                </div>
              </template>
              <span v-else>{{ value }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Stats -->
    <div class="border border-muted/20 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/5 transition-colors"
        @click="showStats = !showStats"
      >
        <UIcon
          :name="showStats ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-muted"
        />
        <span class="text-sm font-medium">Stats</span>
      </button>
      <div v-if="showStats" class="px-3 pb-3 space-y-2 text-sm">
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Ads</span>
          <span class="font-semibold">{{ importJob.num_ads?.toLocaleString() ?? '-' }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Duration</span>
          <span>{{
            formatDuration(
              importJob.job_runs?.at(0)?.started_ts ?? importJob.scheduled_ts,
              importJob.completed_ts,
            )
          }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Started</span>
          <span>{{ formatDateTime(importJob.scheduled_ts) }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Completed</span>
          <span>{{ formatDateTime(importJob.completed_ts) }}</span>
        </div>
      </div>
    </div>

    <!-- Links -->
    <div v-if="hasLinks" class="border border-muted/20 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/5 transition-colors"
        @click="showLinks = !showLinks"
      >
        <UIcon
          :name="showLinks ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-muted"
        />
        <span class="text-sm font-medium">Links</span>
      </button>
      <div v-if="showLinks" class="px-3 pb-3 space-y-2 text-sm">
        <div v-if="importJob.scraping_url" class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Ad Library</span>
          <UButton
            icon="i-lucide-external-link"
            size="xs"
            color="neutral"
            variant="ghost"
            as="a"
            :href="importJob.scraping_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </UButton>
        </div>
        <div v-if="cloudRunUrl" class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Cloud Run</span>
          <UButton
            icon="i-lucide-external-link"
            size="xs"
            color="neutral"
            variant="ghost"
            as="a"
            :href="cloudRunUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </UButton>
        </div>
        <div v-if="cloudRunLogsUrl" class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Cloud Run Logs</span>
          <UButton
            icon="i-lucide-external-link"
            size="xs"
            color="neutral"
            variant="ghost"
            as="a"
            :href="cloudRunLogsUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </UButton>
        </div>
        <div v-if="apifyUrl" class="flex items-center justify-between gap-2">
          <span class="text-muted shrink-0">Apify</span>
          <UButton
            icon="i-lucide-external-link"
            size="xs"
            color="neutral"
            variant="ghost"
            as="a"
            :href="apifyUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </UButton>
        </div>
      </div>
    </div>

    <!-- Details -->
    <div class="border border-muted/20 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/5 transition-colors"
        @click="showDetails = !showDetails"
      >
        <UIcon
          :name="showDetails ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-muted"
        />
        <span class="text-sm font-medium">Details</span>
      </button>
      <div v-if="showDetails" class="px-3 pb-3 space-y-2 text-xs font-mono">
        <div class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">Job ID</span>
          <span class="truncate flex-1">{{ importJob.id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.id)"
          />
        </div>
        <div v-if="importJob.successful_run_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">Successful Run ID</span>
          <span class="truncate flex-1">{{ importJob.successful_run_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.successful_run_id)"
          />
        </div>
        <div v-if="importJob.batch_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">Batch ID</span>
          <span class="truncate flex-1">{{ importJob.batch_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.batch_id)"
          />
        </div>
        <div v-if="importJob.cloud_run_job_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">CR Job ID</span>
          <span class="truncate flex-1">{{ importJob.cloud_run_job_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.cloud_run_job_id)"
          />
        </div>
        <div v-if="importJob.cloud_run_execution_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">CR Execution ID</span>
          <span class="truncate flex-1">{{ importJob.cloud_run_execution_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.cloud_run_execution_id)"
          />
        </div>
        <div v-if="importJob.task_ix != null" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">CR Task Index</span>
          <span class="truncate flex-1">{{ importJob.task_ix }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(String(importJob.task_ix))"
          />
        </div>
        <div v-if="importJob.apify_platform_run_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">Apify Run ID</span>
          <span class="truncate flex-1">{{ importJob.apify_platform_run_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.apify_platform_run_id)"
          />
        </div>
        <div v-if="importJob.apify_platform_dataset_id" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">Apify Dataset ID</span>
          <span class="truncate flex-1">{{ importJob.apify_platform_dataset_id }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.apify_platform_dataset_id)"
          />
        </div>
        <div v-if="importJob.gcs_uri" class="flex items-center gap-2">
          <span class="text-muted w-32 shrink-0">GCS URI</span>
          <span class="truncate flex-1">{{ importJob.gcs_uri }}</span>
          <UButton
            icon="i-lucide-copy"
            size="xs"
            variant="ghost"
            class="shrink-0"
            @click="copyToClipboard(importJob.gcs_uri)"
          />
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="importJob.error_message" class="border border-error/30 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-error/5 transition-colors"
        @click="showError = !showError"
      >
        <UIcon
          :name="showError ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-error"
        />
        <span class="text-sm font-medium text-error">Error</span>
      </button>
      <pre
        v-if="showError"
        class="text-xs bg-error/10 text-error p-3 mx-3 mb-3 rounded overflow-x-auto whitespace-pre-wrap"
        >{{ importJob.error_message }}</pre
      >
    </div>

    <!-- Run History -->
    <div class="border border-muted/20 rounded-lg">
      <button
        class="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/5 transition-colors"
        @click="showRunHistory = !showRunHistory"
      >
        <UIcon
          :name="showRunHistory ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          class="text-muted"
        />
        <span class="text-sm font-medium">Run History</span>
        <UBadge color="neutral" variant="subtle" size="xs">{{ importJob.job_runs.length }}</UBadge>
      </button>
      <div v-if="showRunHistory" class="px-3 pb-3">
        <div v-if="jobRunsWithUrls.length === 0" class="text-sm text-muted py-2">
          No run history available
        </div>
        <div v-else class="space-y-3">
          <!-- Each Run in separate box with submenus -->
          <div
            v-for="(run, index) in jobRunsWithUrls"
            :key="run.id"
            class="border border-muted/20 rounded-lg overflow-hidden"
          >
            <!-- Run Header -->
            <button
              class="w-full flex items-center gap-2 p-3 bg-muted/5 hover:bg-muted/10 transition-colors"
              @click="toggleRunExpanded(run.id)"
            >
              <UIcon
                :name="isRunExpanded(run.id) ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                class="text-muted"
              />
              <span class="text-sm font-medium">Run {{ index + 1 }}</span>
              <UBadge :color="getImportStatusColor(run.status)" size="sm">{{ run.status }}</UBadge>
              <span class="text-sm text-muted">{{ run.method || '-' }}</span>
            </button>

            <div v-if="isRunExpanded(run.id)" class="p-2 space-y-2">
              <!-- Run Stats -->
              <div class="border border-muted/20 rounded-lg">
                <button
                  class="w-full flex items-center gap-2 p-2 text-left hover:bg-muted/5 transition-colors"
                  @click="toggleRunSection(run.id, 'stats')"
                >
                  <UIcon
                    :name="
                      getRunSections(run.id).stats
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                    class="text-muted"
                  />
                  <span class="text-xs font-medium">Stats</span>
                </button>
                <div v-if="getRunSections(run.id).stats" class="px-2 pb-2 space-y-1 text-xs">
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-muted">Ads</span>
                    <span class="font-semibold">{{ run.num_ads?.toLocaleString() ?? '-' }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-muted">Duration</span>
                    <span>{{ run.duration }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-muted">Started</span>
                    <span>{{ formatDateTime(run.started_ts) }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-muted">Completed</span>
                    <span>{{ formatDateTime(run.completed_ts) }}</span>
                  </div>
                </div>
              </div>

              <!-- Run Links -->
              <div
                v-if="run.cloudRunUrl || run.cloudRunLogsUrl || run.apifyUrl"
                class="border border-muted/20 rounded-lg"
              >
                <button
                  class="w-full flex items-center gap-2 p-2 text-left hover:bg-muted/5 transition-colors"
                  @click="toggleRunSection(run.id, 'links')"
                >
                  <UIcon
                    :name="
                      getRunSections(run.id).links
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                    class="text-muted"
                  />
                  <span class="text-xs font-medium">Links</span>
                </button>
                <div v-if="getRunSections(run.id).links" class="px-2 pb-2 space-y-1 text-xs">
                  <div v-if="run.cloudRunUrl" class="flex items-center justify-between gap-2">
                    <span class="text-muted">Cloud Run</span>
                    <UButton
                      icon="i-lucide-external-link"
                      size="xs"
                      color="neutral"
                      variant="ghost"
                      as="a"
                      :href="run.cloudRunUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Open</UButton
                    >
                  </div>
                  <div v-if="run.cloudRunLogsUrl" class="flex items-center justify-between gap-2">
                    <span class="text-muted">Cloud Run Logs</span>
                    <UButton
                      icon="i-lucide-external-link"
                      size="xs"
                      color="neutral"
                      variant="ghost"
                      as="a"
                      :href="run.cloudRunLogsUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Open</UButton
                    >
                  </div>
                  <div v-if="run.apifyUrl" class="flex items-center justify-between gap-2">
                    <span class="text-muted">Apify</span>
                    <UButton
                      icon="i-lucide-external-link"
                      size="xs"
                      color="neutral"
                      variant="ghost"
                      as="a"
                      :href="run.apifyUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Open</UButton
                    >
                  </div>
                </div>
              </div>

              <!-- Run Details -->
              <div class="border border-muted/20 rounded-lg">
                <button
                  class="w-full flex items-center gap-2 p-2 text-left hover:bg-muted/5 transition-colors"
                  @click="toggleRunSection(run.id, 'details')"
                >
                  <UIcon
                    :name="
                      getRunSections(run.id).details
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                    class="text-muted"
                  />
                  <span class="text-xs font-medium">Details</span>
                </button>
                <div
                  v-if="getRunSections(run.id).details"
                  class="px-2 pb-2 space-y-1 text-xs font-mono"
                >
                  <div class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">Run ID</span>
                    <span class="truncate flex-1">{{ run.id }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(run.id)"
                    />
                  </div>
                  <div v-if="run.cloud_run_job_id" class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">CR Job ID</span>
                    <span class="truncate flex-1">{{ run.cloud_run_job_id }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(run.cloud_run_job_id)"
                    />
                  </div>
                  <div v-if="run.cloud_run_execution_id" class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">CR Execution ID</span>
                    <span class="truncate flex-1">{{ run.cloud_run_execution_id }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(run.cloud_run_execution_id)"
                    />
                  </div>
                  <div v-if="run.task_ix != null" class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">CR Task Index</span>
                    <span class="truncate flex-1">{{ run.task_ix }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(String(run.task_ix))"
                    />
                  </div>
                  <div v-if="run.apify_platform_run_id" class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">Apify Run ID</span>
                    <span class="truncate flex-1">{{ run.apify_platform_run_id }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(run.apify_platform_run_id)"
                    />
                  </div>
                  <div v-if="run.apify_platform_dataset_id" class="flex items-center gap-2">
                    <span class="text-muted w-28 shrink-0">Apify Dataset ID</span>
                    <span class="truncate flex-1">{{ run.apify_platform_dataset_id }}</span>
                    <UButton
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      class="shrink-0"
                      @click="copyToClipboard(run.apify_platform_dataset_id)"
                    />
                  </div>
                </div>
              </div>

              <!-- Run Error -->
              <div v-if="run.error_message" class="border border-error/30 rounded-lg">
                <button
                  class="w-full flex items-center gap-2 p-2 text-left hover:bg-error/5 transition-colors"
                  @click="toggleRunSection(run.id, 'error')"
                >
                  <UIcon
                    :name="
                      getRunSections(run.id).error
                        ? 'i-lucide-chevron-down'
                        : 'i-lucide-chevron-right'
                    "
                    class="text-error"
                  />
                  <span class="text-xs font-medium text-error">Error</span>
                </button>
                <pre
                  v-if="getRunSections(run.id).error"
                  class="text-xs bg-error/10 text-error p-2 mx-2 mb-2 rounded overflow-x-auto whitespace-pre-wrap"
                  >{{ run.error_message }}</pre
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
